import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Page } from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { ArrowRight, Briefcase, CalendarBlank, Clock, Coins, EnvelopeSimple, Phone, User, IdentificationCard, VideoCamera } from "@phosphor-icons/react";
import TrailBreadcrumb from "@/components/TrailBreadcrumb";
import TrailBackPill from "@/components/TrailBackPill";
import PageLoader from "@/components/PageLoader";

// Sections accessible from the booking-detail page. Includes `dashboard` so
// the trail can walk back to the member portal hero, plus the directories
// for users who might have jumped in via a deep link.
const MEMBER_TRAIL_SECTIONS = {
  dashboard: { label: "My Dashboard", href: "/dashboard" },
  therapists: { label: "Therapists", href: "/therapists" },
  mentors: { label: "Mentors", href: "/mentors" },
  shop: { label: "Shop", href: "/shop" },
  events: { label: "Events", href: "/events" },
};

const STATUS_TONE = {
  confirmed: { bg: "rgba(42, 92, 54, 0.08)", color: "var(--md-primary)", label: "Confirmed" },
  paid: { bg: "rgba(42, 92, 54, 0.08)", color: "var(--md-primary)", label: "Paid" },
  pending_payment: { bg: "rgba(244, 162, 97, 0.18)", color: "#A45A1A", label: "Awaiting payment" },
  pending: { bg: "rgba(244, 162, 97, 0.18)", color: "#A45A1A", label: "Pending" },
  cancelled: { bg: "rgba(176, 70, 70, 0.1)", color: "#B04646", label: "Cancelled" },
  completed: { bg: "rgba(91, 99, 96, 0.12)", color: "var(--md-text-soft)", label: "Completed" },
};

function fmtDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function BookingDetail() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!user) return;
    setFetching(true);
    api.get("/bookings/me")
      .then((r) => {
        const match = (r.data || []).find((b) => b.id === id);
        if (!match) {
          toast.error("Booking not found");
          navigate("/dashboard", { replace: true });
          return;
        }
        setBooking(match);
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Couldn't load booking");
        navigate("/dashboard", { replace: true });
      })
      .finally(() => setFetching(false));
  }, [id, user, navigate]);

  if (loading || fetching) {
    return <Page><PageLoader testid="booking-detail-loading" /></Page>;
  }
  if (!user || !booking) return null;

  const tone = STATUS_TONE[booking.status] || { bg: "var(--md-bg-alt)", color: "var(--md-text-soft)", label: booking.status };

  return (
    <Page>
      <section className="md-section" style={{ background: "var(--md-bg-alt)" }}>
        <div className="md-container">
          <TrailBackPill
            scope="md.member.trail"
            currentId="booking_detail"
            sections={MEMBER_TRAIL_SECTIONS}
            className="mb-3"
          />
          <div className="md-label-eyebrow mb-3">Booking detail</div>
          <h1 className="text-3xl md:text-4xl font-serif mb-2">{booking.provider_name || "Your MindDhara session"}</h1>
          <p className="text-base" style={{ color: "var(--md-text-soft)" }}>
            <span style={{ textTransform: "capitalize" }}>{booking.session_type || "Session"}</span> · {booking.provider_type}
          </p>
          <TrailBreadcrumb
            scope="md.member.trail"
            current="Booking detail"
            currentId="booking_detail"
            homeLabel="Home"
            homeHref="/"
            sections={MEMBER_TRAIL_SECTIONS}
            className="mt-3"
          />
        </div>
      </section>

      <section className="md-section">
        <div className="md-container">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md-card md:col-span-2" data-testid="booking-detail-card">
              <div className="flex items-start gap-4">
                {booking.provider_photo ? (
                  <img src={booking.provider_photo} alt={booking.provider_name} className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "var(--md-bg-alt)" }}>
                    <Briefcase size={24} weight="duotone" style={{ color: "var(--md-primary)" }} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="md-label-eyebrow mb-1.5">Status</div>
                  <span
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium"
                    style={{ background: tone.bg, color: tone.color }}
                    data-testid="booking-status-pill"
                  >
                    {tone.label}
                  </span>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-6 border-t" style={{ borderColor: "var(--md-border)" }}>
                <DetailRow icon={CalendarBlank} label="Date" value={fmtDate(booking.date)} testid="row-date" />
                <DetailRow icon={Clock} label="Time" value={booking.time_slot || ""} testid="row-time" />
                <DetailRow icon={Coins} label="Amount" value={format(booking.amount || 0)} testid="row-amount" />
                <DetailRow icon={IdentificationCard} label="Booking ID" value={booking.id?.slice(0, 12)} mono testid="row-id" />
              </div>

              {booking.meet_link && (
                <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--md-border)" }}>
                  <div className="md-label-eyebrow mb-2">Video session</div>
                  <a href={booking.meet_link} target="_blank" rel="noopener noreferrer" className="btn-primary text-sm" data-testid="booking-meet-link">
                    <VideoCamera size={16} weight="bold" /> Join Google Meet
                  </a>
                </div>
              )}

              {booking.notes && (
                <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--md-border)" }}>
                  <div className="md-label-eyebrow mb-2">Your notes</div>
                  <p className="text-sm" style={{ color: "var(--md-text-soft)" }}>{booking.notes}</p>
                </div>
              )}
            </div>

            <aside className="md-card" data-testid="booking-detail-side">
              <div className="md-label-eyebrow mb-3">Need help?</div>
              <h3 className="font-serif text-lg mb-2">Talk to MindDhara</h3>
              <p className="text-sm mb-4" style={{ color: "var(--md-text-soft)" }}>Questions about rescheduling, cancellation or your provider? We&apos;re here.</p>
              <div className="space-y-2.5">
                <a href="mailto:hello@minddhara.com" className="md-trail-side-link" data-testid="contact-email">
                  <EnvelopeSimple size={14} weight="duotone" /> hello@minddhara.com
                </a>
                <a href="tel:+919999999999" className="md-trail-side-link" data-testid="contact-phone">
                  <Phone size={14} weight="duotone" /> +91 99999 99999
                </a>
                <Link to="/dashboard" className="btn-outline text-sm w-full mt-4 justify-center" data-testid="back-to-dashboard">
                  <User size={14} weight="bold" /> Back to dashboard <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </Page>
  );
}

const DetailRow = ({ icon: Icon, label, value, testid, mono }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--md-bg-alt)" }}>
      <Icon size={17} weight="duotone" style={{ color: "var(--md-primary)" }} />
    </div>
    <div className="min-w-0">
      <div className="md-label-eyebrow">{label}</div>
      <div className={`text-base ${mono ? "font-mono text-sm" : ""} truncate`}>{value || "—"}</div>
    </div>
  </div>
);
